"use client";

import { useEffect, useMemo, useState } from "react";

type TimeLeft = { d: number; h: number; m: number; s: number };

const ZERO: TimeLeft = { d: 0, h: 0, m: 0, s: 0 };

function getTimeLeft(target: Date): TimeLeft {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return ZERO;
  const s = Math.floor(diff / 1000);
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
  };
}

function pad(n: number, width: number = 2) {
  return String(n).padStart(width, "0");
}

function Cell({
  value,
  label,
  width = 2,
  pulse = false,
}: {
  value: number;
  label: string;
  width?: number;
  pulse?: boolean;
}) {
  return (
    <div className="flex flex-col items-center">
      <span
        className={`inline-block text-center font-numeric tabular-nums text-fg text-4xl sm:text-5xl lg:text-6xl ${
          pulse ? "animate-pulse" : ""
        }`}
        style={{ minWidth: `${width}ch`, fontVariantNumeric: "tabular-nums" }}
      >
        {pad(value, width)}
      </span>
      <span className="mt-3 font-display text-[9px] uppercase tracking-[0.3em] text-fg-faint">
        {label}
      </span>
    </div>
  );
}

function Sep() {
  return (
    <span
      aria-hidden
      className="pb-7 font-numeric text-4xl text-gold-dark sm:pb-8 sm:text-5xl lg:text-6xl"
    >
      :
    </span>
  );
}

// Sprint 9 — eventLabel is the full display line (event name · date), built
// by the caller from the same source as eventDate, so the human label can
// never contradict the ticker. No hardcoded event name in here.
export function HeroCountdown({
  eventDate,
  eventLabel,
}: {
  eventDate: string;
  eventLabel: string;
}) {
  const [tick, setTick] = useState<TimeLeft>(ZERO);
  const target = useMemo(() => new Date(eventDate), [eventDate]);

  useEffect(() => {
    const initial = setTimeout(() => setTick(getTimeLeft(target)), 0);
    const id = setInterval(() => setTick(getTimeLeft(target)), 1000);
    return () => {
      clearTimeout(initial);
      clearInterval(id);
    };
  }, [target]);

  return (
    <div className="tr-trophy-frame relative rounded-sm border border-gold/40 bg-base/70 p-6 backdrop-blur-md sm:p-8">
      <span aria-hidden className="absolute -left-px -top-px h-3 w-3 border-l-2 border-t-2 border-gold" />
      <span aria-hidden className="absolute -right-px -top-px h-3 w-3 border-r-2 border-t-2 border-gold" />
      <span aria-hidden className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-gold" />
      <span aria-hidden className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-gold" />

      <p className="flex items-center gap-2 font-display text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">
        <span aria-hidden className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gold" />
        </span>
        First whistle in
      </p>

      <div className="mt-8 flex items-end justify-between gap-2 sm:gap-4 lg:gap-6">
        <Cell value={tick.d} label="Days" width={3} />
        <Sep />
        <Cell value={tick.h} label="Hours" />
        <Sep />
        <Cell value={tick.m} label="Min" />
        <Sep />
        <Cell value={tick.s} label="Sec" pulse />
      </div>

      <div className="mt-7 h-px bg-line" />

      <p className="mt-5 font-display text-[10px] uppercase tracking-[0.3em] text-fg-faint">
        {eventLabel}
      </p>
    </div>
  );
}
